import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import OpportunityCard from "@/components/OpportunityCard";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Check, CalendarPlus, Share2, RotateCcw } from "lucide-react";
import HomeScreenTip from "@/components/HomeScreenTip";
import CoachNudge from "@/components/CoachNudge";
import WinCardModal from "@/components/WinCardModal";
import { CoachStartCard } from "@/components/CoachStartCard";
import { QuizExtraCard } from "@/components/QuizExtraCard";
import { DailyQuote } from "@/components/DailyQuote";
import { HideConfirm, HiddenPaths } from "@/components/HidePaths";
import { clearLocalProgress, getLocalProgress, setLocalProgress } from "@/lib/progress";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const MODES = [
  { id: null, label: "All", desc: "Every path" },
  { id: "easy", label: "Easy Mode", desc: "Simplest realistic ways to earn extra" },
  { id: "side_hustle", label: "Side Hustle", desc: "Income beside your job" },
  { id: "escape", label: "Escape Mode", desc: "Replace your job income" },
  { id: "build", label: "Build Mode", desc: "Serious scalable business" },
];

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null);
  const [introvert, setIntrovert] = useState(false);
  const [compare, setCompare] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const loadReq = useRef(0);
  const [playlists, setPlaylists] = useState([]);
  const [recaps, setRecaps] = useState([]);
  const [win, setWin] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    const req = ++loadReq.current;
    setLoading(true);
    setLoadFailed(false);
    try {
      const pending = getLocalProgress().pending_quiz;
      if (pending) {
        await api.post("/quiz", pending, { timeout: 20000 });
        setLocalProgress({ pending_quiz: null });
      }
      const { data } = await api.get("/opportunities", { params: { mode: mode || undefined, introvert }, timeout: 20000 });
      if (req === loadReq.current) { setOpps(data.opportunities); setSwapsLeft(data.swaps_left); setHiddenCount(data.hidden_count || 0); }
    } catch (e) {
      if (e?.response?.status === 401 || req !== loadReq.current) return;
      setLoadFailed(true);
    } finally { if (req === loadReq.current) setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mode, introvert]);
  useEffect(() => { api.get("/playlists").then(r => setPlaylists(r.data.playlists || [])).catch(()=>{}); }, []);
  useEffect(() => { if (user?.is_premium) api.get("/coach/recaps").then(r => setRecaps(r.data.recaps || [])).catch(()=>{}); }, [user?.is_premium]);

  const [pendingHide, setPendingHide] = useState(null);
  const [swapsLeft, setSwapsLeft] = useState(null);
  const [hiddenCount, setHiddenCount] = useState(0);

  const feedback = async (opportunity_id, action) => {
    if (action !== "like") {
      setPendingHide({ id: opportunity_id, action, title: opps.find(o => o.id === opportunity_id)?.title });
      return;
    }
    try {
      await api.post("/feedback", { opportunity_id, action });
      toast.success("Got it. More like this.");
    } catch { toast.error("Try again"); }
  };

  const confirmHide = async () => {
    const p = pendingHide;
    setPendingHide(null);
    try {
      await api.post("/feedback", { opportunity_id: p.id, action: p.action });
      toast.success("Hidden. Your next best path took its place.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Try again"); }
  };

  const toggleCompare = (id) => {
    setCompare(c => c.includes(id) ? c.filter(x => x !== id) : (c.length >= 3 ? c : [...c, id]));
  };

  const noIdea = async () => {
    const { data } = await api.post("/opportunities/no-idea");
    if (!data.top_three?.length) return toast.error("Take the quiz first.");
    setOpps(data.top_three.map((o, i) => ({ ...o, locked: false })));
    toast.success("Here are your 3 best matches.");
  };

  const toggleRecapDone = async (r) => {
    const next = !r.done;
    setRecaps((rs) => rs.map((x) => (x.id === r.id ? { ...x, done: next } : x)));
    try { await api.post(`/coach/recaps/${r.id}/done`, { done: next }); }
    catch { setRecaps((rs) => rs.map((x) => (x.id === r.id ? { ...x, done: !next } : x))); toast.error("Try again"); }
  };

  const makeCheckin = (r) => nav("/checkin", { state: { focus: r.next_step } });

  const startOver = async () => {
    try {
      await api.post("/progress/reset");
      clearLocalProgress();
      toast.success("Cleared. Fresh start.");
      window.location.href = "/quiz";
    } catch { toast.error("Couldn't reset. Try again."); }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 pt-6">
      <HomeScreenTip />
      <CoachNudge />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="eyebrow text-[#FD0F10]">YOUR ESCAPE PLAN · BUILT FOR YOU</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black mt-1">{user?.name || "Alright"}, let's move.</h1>
          <p className="text-gray-200 text-sm sm:text-base mt-2 leading-relaxed max-w-xl">
            These paths aren't for everyone. They're for <span className="text-white font-bold">you</span>. Your money, your time, your personality. Start with number one. Don't overthink it. We're building this together.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/plan" className="text-xs font-mono-ui uppercase tracking-wider border border-[#FD0F10] text-[#FD0F10] px-3 py-2 rounded hover:bg-[#FD0F10] hover:text-white" data-testid="your-plan-link">Your Plan</Link>
          <Link to="/quiz?retake=1" className="text-xs font-mono-ui uppercase tracking-wider border border-[#2A2E39] px-3 py-2 rounded hover:border-white" data-testid="retake-quiz">Retake quiz</Link>
          <Link to="/referral" className="text-xs font-mono-ui uppercase tracking-wider border border-[#2A2E39] px-3 py-2 rounded hover:border-white" data-testid="referral-link-nav">Refer, earn Pro</Link>
          <Link to="/get-me-out" className="text-xs font-mono-ui uppercase tracking-wider hero-accent px-3 py-2 rounded" data-testid="get-me-out-link">GET ME OUT</Link>
        </div>
      </div>

      <DailyQuote />

      <QuizExtraCard onSaved={load} />

      <CoachStartCard opps={opps} />

      {!user?.is_premium && (
        <div className="mt-5 card-panel-elevated p-4 border-[#FD0F10]/40" data-testid="free-tier-hint">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-start gap-3 flex-1">
              <div className="text-2xl shrink-0" aria-hidden>🔓</div>
              <div className="flex-1">
                <div className="eyebrow text-[#FD0F10]">GO ALL IN, UNLOCK THE FULL PLAN</div>
                <p className="mt-1 text-sm text-gray-100 leading-relaxed">
                  You are seeing your top three. Every other path picked for you, the AI Coach pushing you daily, and the Exit Number tools are one tap away. <span className="text-white font-bold">Don't do this half in.</span>
                </p>
              </div>
            </div>
            <Link to="/pro" className="shrink-0 hero-accent text-white text-xs font-mono-ui uppercase tracking-wider px-4 py-3 sm:py-2 rounded text-center" data-testid="free-tier-upgrade">Unlock the full plan</Link>
          </div>
        </div>
      )}

      {user?.is_premium && recaps.length > 0 && (
        <div className="mt-5 card-panel p-4" data-testid="coach-recaps">
          <div className="flex items-center justify-between">
            <div className="eyebrow text-[#FD0F10]">FROM YOUR VOICE CHATS</div>
            <Link to="/recaps" className="text-xs font-mono-ui uppercase tracking-wider text-gray-400 hover:text-white" data-testid="recaps-see-all">See all</Link>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {recaps.slice(0, 4).map((r) => (
              <div key={r.id} className="bg-[#0B0C0E] border border-[#2A2E39] rounded-lg p-3" data-testid={`recap-${r.id}`}>
                {r.title && <div className="font-display font-bold text-white text-base leading-snug" data-testid={`recap-title-${r.id}`}>{r.title}</div>}
                <p className="text-sm text-gray-300 leading-relaxed mt-1">{r.summary}</p>
                <div className="mt-2 flex items-start gap-2">
                  <span className="eyebrow text-[#FD0F10] mt-0.5 shrink-0">NEXT</span>
                  <p className={`text-sm font-semibold leading-snug ${r.done ? "text-gray-500 line-through" : "text-white"}`}>{r.next_step}</p>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <button
                    onClick={() => toggleRecapDone(r)}
                    className={`flex items-center gap-1.5 text-xs font-mono-ui uppercase tracking-wider px-2.5 py-1.5 rounded border ${r.done ? "border-[#FD0F10] bg-[#FD0F10]/10 text-[#FD0F10]" : "border-[#2A2E39] text-gray-300 hover:border-[#FD0F10]"}`}
                    data-testid={`recap-done-${r.id}`}
                  >
                    <Check size={13} /> {r.done ? "Done" : "Mark done"}
                  </button>
                  <button
                    onClick={() => makeCheckin(r)}
                    className="flex items-center gap-1.5 text-xs font-mono-ui uppercase tracking-wider px-2.5 py-1.5 rounded border border-[#2A2E39] text-gray-300 hover:border-[#FD0F10]"
                    data-testid={`recap-checkin-${r.id}`}
                  >
                    <CalendarPlus size={13} /> Check-in
                  </button>
                  {r.done && (
                    <button
                      onClick={() => setWin(r)}
                      className="flex items-center gap-1.5 text-xs font-mono-ui uppercase tracking-wider px-2.5 py-1.5 rounded border border-[#FD0F10] text-[#FD0F10] hover:bg-[#FD0F10] hover:text-white"
                      data-testid={`recap-share-${r.id}`}
                    >
                      <Share2 size={13} /> Share win
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 flex gap-2 overflow-x-auto pb-2 -mx-5 px-5 no-scrollbar snap-x">
        {MODES.map(m => (
          <button key={m.label} onClick={() => setMode(m.id)} className={`shrink-0 px-4 py-2 rounded-lg border text-sm font-mono-ui uppercase tracking-wider ${mode === m.id ? "border-[#FD0F10] bg-[#FD0F10]/10 text-white" : "border-[#2A2E39] text-gray-400 hover:text-white"}`} data-testid={`mode-${m.id || 'all'}`}>
            {m.label}
          </button>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-4 flex-wrap">
        <label className="flex items-center gap-2 text-sm text-gray-300">
          <input type="checkbox" checked={introvert} onChange={e => setIntrovert(e.target.checked)} className="accent-[#FD0F10] scale-125" data-testid="introvert-toggle" />
          Introvert Mode
        </label>
        <button onClick={noIdea} className="text-sm font-mono-ui uppercase tracking-wider border border-[#FD0F10] text-[#FD0F10] px-3 py-2 rounded hover:bg-[#FD0F10] hover:text-white flex items-center gap-2" data-testid="no-idea-btn">
          <Sparkles size={14}/> I HAVE NO IDEA
        </button>
        {compare.length > 0 && (
          <button onClick={() => nav(`/compare?ids=${compare.join(",")}`)} className="text-sm font-mono-ui uppercase tracking-wider hero-accent px-3 py-2 rounded" data-testid="compare-btn">
            Compare ({compare.length})
          </button>
        )}
      </div>

      {loading ? (
        <div className="mt-8 text-gray-400" data-testid="paths-loading">Loading your paths…</div>
      ) : loadFailed ? (
        <div className="mt-8 card-panel p-8 text-center" data-testid="paths-load-failed">
          <div className="font-display text-xl font-bold">This is taking longer than usual.</div>
          <p className="text-gray-400 mt-2">Your quiz answers are safe. Give it another try.</p>
          <button onClick={load} className="inline-block mt-4 hero-accent px-5 py-3 rounded font-bold" data-testid="paths-retry-btn">Try again</button>
        </div>
      ) : opps.length === 0 ? (
        <div className="mt-8 card-panel p-8 text-center">
          <div className="font-display text-xl font-bold">No matches yet.</div>
          <p className="text-gray-400 mt-2">Take the assessment to see personalized paths.</p>
          <Link to="/quiz?retake=1" className="inline-block mt-4 hero-accent px-5 py-3 rounded font-bold" data-testid="take-quiz-empty">Take the quiz</Link>
        </div>
      ) : (
        <>
          <p className="mt-6 text-xs text-gray-400" data-testid="results-disclaimer">Results depend on your effort. The more hours you put in, the faster it usually grows. Most people earn less at first, and that's normal.</p>
          {!user?.is_premium && (mode || introvert) && opps.filter(o => !o.locked).length < 3 && (
            <p className="mt-2 text-xs text-gray-300" data-testid="free-mode-hint">This view filters your list. All 3 of your free paths are under <button onClick={() => { setMode(null); setIntrovert(false); }} className="underline text-white" data-testid="free-mode-hint-all">All</button>.</p>
          )}
          <div className="mt-2 grid gap-4">
            {opps.map(o => (
              <OpportunityCard key={o.id} opp={o} onFeedback={feedback} compareChecked={compare.includes(o.id)} onToggleCompare={toggleCompare} />
            ))}
          </div>
          <HiddenPaths count={hiddenCount} onChange={load} />
          <HideConfirm pending={pendingHide} swapsLeft={swapsLeft} onCancel={() => setPendingHide(null)} onConfirm={confirmHide} />
          {playlists.length > 0 && (
            <div className="mt-12" data-testid="playlists-section">
              <div className="eyebrow text-[#FD0F10]">CURATED COMBOS</div>
              <h2 className="font-display text-2xl sm:text-3xl font-black mt-1">Not sure where to start?</h2>
              <p className="text-gray-100 text-base mt-2 font-semibold">Start with the one you'd actually enjoy doing.</p>
              <p className="text-gray-300 text-sm mt-1">It's easier to keep going when you don't hate what you're building.</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                {playlists.map(pl => (
                  <div key={pl.id} className="card-panel p-5" data-testid={`playlist-${pl.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-display font-bold text-lg text-white">{pl.title}</div>
                        <p className="text-sm text-gray-300 mt-1">{pl.tagline}</p>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="eyebrow text-[#FD0F10]">FIT</div>
                        <div className="font-display font-black text-xl text-white">{pl.avg_escape_score}%</div>
                      </div>
                    </div>
                    <div className="mt-4 space-y-1.5">
                      {pl.items.map(it => (
                        <Link key={it.id} to={`/opportunity/${it.id}`} className="flex items-center justify-between text-sm text-gray-200 hover:text-white bg-[#0B0C0E] border border-[#2A2E39] rounded px-3 py-2" data-testid={`playlist-${pl.id}-${it.id}`}>
                          <span className="truncate pr-2">{it.title}</span>
                          <span className="text-[#FD0F10] font-mono-ui text-xs">{it.escape_score}%</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      <div className="mt-12 pt-6 border-t border-[#2A2E39] flex justify-center">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button className="flex items-center gap-2 text-xs font-mono-ui uppercase tracking-wider text-gray-500 hover:text-white" data-testid="start-over-btn">
              <RotateCcw size={13} /> Start over
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent className="border-[#2A2E39] bg-[#14161B]" data-testid="start-over-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display">Are you sure?</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-300">
                This clears your quiz answers and progress so you can start fresh. Your account and subscription stay exactly as they are.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-white text-white" data-testid="start-over-cancel">Keep my progress</AlertDialogCancel>
              <AlertDialogAction onClick={startOver} className="bg-[#FD0F10] hover:bg-[#D60D0E] text-white font-bold" data-testid="start-over-confirm">
                Yes, start over
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      <WinCardModal recap={win} onClose={() => setWin(null)} />
    </div>
  );
}
